import React, { useEffect, useMemo, useState } from 'react';

import {
  discountExposure,
  previewAssetCharge,
  rateplanStripeReadiness,
  syncRatePlanToStripe,
  type AssetChargeLine,
  type DiscountExposureRow,
  type RatePlanDetail,
  type StripeReadinessRow,
  type StripeSyncReport,
  type WorkspacePinRow,
} from './api';
import { money, symbolFor } from './money';

/**
 * What customers pay. The default view of the Pricing tab, and the answer to
 * the question this screen is opened for most often.
 *
 * It is read-only on purpose. The old screen answered "what do we charge?" with
 * the same seven-column editable table used to change the charge, so the most
 * common task was performed through the rarest task's interface, and every
 * visit carried the risk of an accidental edit. Reading and changing are two
 * different jobs; this is the reading one.
 *
 * It is also written in the words a price gets described in out loud -- "the
 * first 25 assets, £3 each" rather than a band table with min/max columns --
 * because the person reading it is about to repeat it to a customer.
 */

function ordinalRange(min: number, max: number | null): string {
  if (min <= 1 && max != null) return `The first ${max} assets`;
  if (max == null) return `Asset ${min} and every one after it`;
  if (min === max) return `Asset ${min}`;
  return `Assets ${min} to ${max}`;
}

/**
 * Sub-penny unit prices need more than two decimals, but never fewer: money
 * reads as money at two, and '£0.1' looks like a rounding slip.
 */
function perUnit(totalMinor: number, units: number, currency: string): string {
  const each = totalMinor / units / 100;
  const text = each.toFixed(each < 0.1 ? 4 : 3).replace(/(\.\d\d\d?)0+$/, '$1');
  return `${symbolFor(currency)}${text}`;
}

/** '1' is the listed rate; anything else is worth saying as a percentage. */
function rateNote(multiplier: number): string | null {
  if (!Number.isFinite(multiplier) || multiplier === 1) return null;
  const delta = Math.round((multiplier - 1) * 1000) / 10;
  return delta > 0 ? `${delta}% above the listed rate` : `${Math.abs(delta)}% below the listed rate`;
}

export default function PriceSheet({
  plan,
  pins,
  currencies,
  siteCurrency,
  onPresentOnSite,
  onChangePrices,
  onShowPromos,
  draftExists,
}: {
  plan: RatePlanDetail;
  pins: WorkspacePinRow[];
  currencies: string[];
  siteCurrency: string | null;
  onPresentOnSite: (currency: string) => void;
  onChangePrices: () => void;
  onShowPromos: () => void;
  draftExists: boolean;
}) {
  const cur = plan.currency;
  const live = plan.status === 'published';

  // How many customers are actually on each plan. Without this the four
  // editions read as the main event; with it, the truth (everybody is billed on
  // assets alone) is visible in the place where it changes a decision.
  const onEdition = useMemo(() => {
    const counts = new Map<string, number>();
    for (const pin of pins) {
      if (pin.currency !== cur) continue;
      const key = pin.edition_key ?? '—';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [pins, cur]);

  const anyOnAnEdition = plan.editions.some((e) => (onEdition.get(e.plan_key) ?? 0) > 0);

  // --- The worked example ----------------------------------------------------
  // Priced by the server function that bills, so the sentence on this sheet and
  // the number on the invoice cannot disagree.

  const [assets, setAssets] = useState('100');
  const [editionKey, setEditionKey] = useState(
    plan.editions.find((e) => e.is_featured)?.plan_key ?? plan.editions[0]?.plan_key ?? '',
  );
  const [lines, setLines] = useState<AssetChargeLine[] | null>(null);
  const edition = plan.editions.find((e) => e.plan_key === editionKey) ?? null;

  useEffect(() => {
    const count = Number(assets.trim());
    if (!Number.isInteger(count) || count < 0 || !editionKey) {
      setLines(null);
      return;
    }
    let alive = true;
    void previewAssetCharge(plan.id, editionKey, count)
      .then((next) => { if (alive) setLines(next); })
      .catch(() => { if (alive) setLines(null); });
    return () => { alive = false; };
  }, [plan.id, plan.content_hash, assets, editionKey]);

  const assetCharge = useMemo(
    () => (lines ?? []).reduce((sum, l) => sum + l.subtotal_minor, 0),
    [lines],
  );
  const fee = edition?.monthly_price_minor ?? 0;

  // --- Where these prices are live -------------------------------------------

  const [stripe, setStripe] = useState<StripeReadinessRow[] | null>(null);
  const [stripeCheck, setStripeCheck] = useState(0);
  useEffect(() => {
    let alive = true;
    void rateplanStripeReadiness(plan.id, 'live')
      .then((rows) => { if (alive) setStripe(rows); })
      .catch(() => { if (alive) setStripe(null); });
    return () => { alive = false; };
  }, [plan.id, stripeCheck]);

  // Preview first, always: the apply button only exists once a preview has
  // shown exactly what would be created in the connected Stripe account.
  const [sync, setSync] = useState<StripeSyncReport | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const runSync = async (apply: boolean) => {
    if (apply && !window.confirm(
      `Create these prices in your ${sync?.mode ?? ''} Stripe account? Existing prices are never changed or deleted.`,
    )) return;
    setSyncBusy(true);
    setSyncError(null);
    try {
      setSync(await syncRatePlanToStripe(plan.id, apply));
      if (apply) setStripeCheck((n) => n + 1);
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncBusy(false);
    }
  };
  const stripeReady = (stripe ?? []).filter((r) => r.present && !r.drifted).length;

  // What is being given away. On the sheet because "what customers pay" is not
  // honestly answered by a price list alone once anybody is on a discount.
  const [exposure, setExposure] = useState<DiscountExposureRow | null>(null);
  useEffect(() => {
    let alive = true;
    void discountExposure(cur)
      .then((rows) => { if (alive) setExposure(rows[0] ?? null); })
      .catch(() => { if (alive) setExposure(null); });
    return () => { alive = false; };
  }, [cur]);

  const [showAllActions, setShowAllActions] = useState(false);
  const listedActions = plan.credit_costs.filter((c) => c.public_key);
  const unlistedActions = plan.credit_costs.filter((c) => !c.public_key);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          {live ? (
            <>
              <p className="lede">These are the prices a new customer gets today, in <b>{cur}</b>.</p>
              <p className="sub" style={{ margin: '4px 0 0' }}>
                Live since {plan.published_at
                  ? new Date(plan.published_at).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })
                  : 'the start'}
                {' · '}
                {plan.workspace_count} customer{plan.workspace_count === 1 ? '' : 's'} on them.
              </p>
            </>
          ) : (
            <>
              <p className="lede">
                You are looking at <b>version {plan.version}</b> ({cur}), which is not the live
                price list.
              </p>
              <p className="sub" style={{ margin: '4px 0 0' }}>
                {plan.workspace_count} customer{plan.workspace_count === 1 ? ' is' : 's are'} still on
                it, and will stay on it until something deliberately moves them.
              </p>
            </>
          )}
        </div>
        <button className="btn primary" onClick={onChangePrices}>
          {draftExists ? 'Continue the price change' : 'Change these prices'}
        </button>
      </div>

      {exposure && exposure.discounted_workspaces > 0 ? (
        <div className="note-bar" style={{ marginBottom: 16 }}>
          <div>
            <b>
              {exposure.discounted_workspaces} customer
              {exposure.discounted_workspaces === 1 ? ' is' : 's are'} on a promo discount.
            </b>
            That is <b>{money(exposure.discount_given_minor, cur)}</b> a month not being collected
            at these prices.
            {exposure.ending_within_90d > 0 ? (
              <>
                {' '}
                <b>{exposure.ending_within_90d}</b> of those discounts
                {exposure.ending_within_90d === 1 ? ' ends' : ' end'} within ninety days, and
                {exposure.ending_within_90d === 1 ? ' that bill rises' : ' those bills rise'} on
                {exposure.ending_within_90d === 1 ? ' its' : ' their'} own.
              </>
            ) : null}
          </div>
          <div className="spacer" />
          <button className="btn" onClick={onShowPromos}>Promo codes</button>
        </div>
      ) : null}

      {draftExists ? (
        <div className="note-bar" style={{ marginBottom: 16 }}>
          <div>
            <b>A price change is part-finished.</b>
            Nobody is paying it yet — a change only takes effect when you make it live.
          </div>
          <div className="spacer" />
          <button className="btn" onClick={onChangePrices}>Pick it up</button>
        </div>
      ) : null}

      <div className="sheet">
        {/* -- Assets: what actually bills, so it goes first ------------------ */}
        <div className="sheet-sec">
          <h3>Assets</h3>
          <p className="say">
            Every customer pays for the assets they have, every month. The rate steps down as they add
            more, and each step only applies to the assets inside it.
          </p>
          {plan.bands.length ? (
            plan.bands.map((b) => (
              <div className="line" key={b.min_assets}>
                <span className="what">{ordinalRange(b.min_assets, b.max_assets)}</span>
                <span className="lead" />
                <span className="fig">{money(b.unit_minor, cur)} each</span>
              </div>
            ))
          ) : (
            <p className="muted">No asset prices are set, so this version cannot bill anybody.</p>
          )}

          <div className="calc">
            A customer with{' '}
            <input
              className="num"
              value={assets}
              onChange={(e) => setAssets(e.target.value)}
              aria-label="Number of assets"
            />{' '}
            assets on{' '}
            <select value={editionKey} onChange={(e) => setEditionKey(e.target.value)} aria-label="Plan">
              {plan.editions.map((e) => (
                <option key={e.plan_key} value={e.plan_key}>{e.display_name}</option>
              ))}
            </select>{' '}
            {lines ? (
              edition?.is_contact_sales ? (
                <>pays whatever was negotiated — {edition.display_name} is a contact-sales plan.</>
              ) : (
                <>
                  pays <b>{money(assetCharge, cur)}</b> for assets
                  {fee ? <> plus a <b>{money(fee, cur)}</b> plan fee</> : null}
                  {' '}= <b>{money(assetCharge + fee, cur)}</b> a month.
                </>
              )
            ) : (
              <span className="muted">— enter a whole number of assets.</span>
            )}
          </div>
          <p className="hint">
            Worked out by the same database function that produces the invoice, so this cannot drift
            from what is actually charged.
          </p>
        </div>

        {/* -- Plans ---------------------------------------------------------- */}
        <div className="sheet-sec">
          <h3>Plans</h3>
          <p className="say">
            What a customer pays before any assets, and what the plan brings with it.
          </p>
          <div className="matrix-scroll" style={{ boxShadow: 'none' }}>
            <table className="matrix">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th style={{ textAlign: 'right' }}>A month</th>
                  <th style={{ textAlign: 'right' }}>A year</th>
                  <th style={{ textAlign: 'right' }}>AI credits per asset</th>
                  <th>Asset rate</th>
                  <th style={{ textAlign: 'right' }}>Customers</th>
                </tr>
              </thead>
              <tbody>
                {plan.editions.map((e) => {
                  const count = onEdition.get(e.plan_key) ?? 0;
                  const note = rateNote(Number(e.asset_rate_multiplier));
                  return (
                    <tr key={e.plan_key} style={{ opacity: e.is_active ? 1 : 0.55 }}>
                      <td>
                        <span className="feat-name">{e.display_name}</span>
                        {e.is_featured ? (
                          <span className="pill" style={{ marginLeft: 8 }}>recommended</span>
                        ) : null}
                        {!e.is_active ? <span className="pill" style={{ marginLeft: 8 }}>off</span> : null}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {e.is_contact_sales ? <span className="muted">talk to sales</span> : money(e.monthly_price_minor, cur)}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {e.is_contact_sales ? <span className="muted">—</span>
                          : e.annual_price_minor == null ? <span className="muted">not offered</span>
                          : money(e.annual_price_minor, cur)}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>{e.included_credits_per_asset}</td>
                      <td className={note ? undefined : 'muted'}>{note ?? 'as listed above'}</td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {count || <span className="muted">none</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!anyOnAnEdition ? (
            <p className="hint">
              Nobody is on one of these plans today — every customer is billed on assets alone. Worth
              knowing before spending time on the plan fees above.
            </p>
          ) : null}
        </div>

        {/* -- Trial ---------------------------------------------------------- */}
        <div className="sheet-sec">
          <h3>Starting out</h3>
          {plan.trial ? (
            <p className="say" style={{ marginBottom: 0 }}>
              A new workspace gets <b>{plan.trial.trial_days} days free</b>
              {plan.trial.trial_asset_cap == null
                ? ' with no limit on how many assets it adds'
                : plan.trial.trial_asset_cap === 1
                  ? <> and can have <b>one asset</b> while trialing</>
                  : <> and can have up to <b>{plan.trial.trial_asset_cap} assets</b> while trialing</>}
              .{' '}
              {plan.trial.free_first_assets === 1 ? (
                <>After that, its <b>first asset</b> is always free.</>
              ) : plan.trial.free_first_assets > 1 ? (
                <>After that, its first <b>{plan.trial.free_first_assets} assets</b> are always free.</>
              ) : (
                <>After that it pays from its first asset.</>
              )}{' '}
              A customer keeps the trial terms of the price list it signed up under.
            </p>
          ) : (
            <p className="muted" style={{ margin: 0 }}>No trial is set on this version.</p>
          )}
        </div>

        {/* -- Credits -------------------------------------------------------- */}
        <div className="sheet-sec">
          <h3>AI credits</h3>
          <p className="say">
            Plans include credits per asset each month. When those run out a customer can buy more.
          </p>
          {plan.credit_packs.length ? (
            plan.credit_packs.map((k) => (
              <div className="line" key={k.sku}>
                <span className="what">{k.units.toLocaleString()} credits</span>
                <span className="lead" />
                <span className="fig">
                  {money(k.price_minor, cur)}
                  <span className="note num">{perUnit(k.price_minor, k.units, cur)} a credit</span>
                </span>
              </div>
            ))
          ) : (
            <p className="muted">No top-up bundles are on sale in this version.</p>
          )}

          <p className="say" style={{ margin: '18px 0 8px' }}>What each AI action costs a customer:</p>
          {listedActions.map((c) => (
            <div className="line" key={c.action_key}>
              <span className="what">{c.display_label ?? c.public_key ?? c.action_key}</span>
              <span className="lead" />
              <span className="fig num">{c.credits} credit{c.credits === 1 ? '' : 's'}</span>
            </div>
          ))}
          {showAllActions
            ? unlistedActions.map((c) => (
                <div className="line" key={c.action_key}>
                  <span className="what">
                    {c.display_label ?? c.action_key}
                    <span className="note">not shown on the website</span>
                  </span>
                  <span className="lead" />
                  <span className="fig num">{c.credits} credit{c.credits === 1 ? '' : 's'}</span>
                </div>
              ))
            : null}
          {unlistedActions.length ? (
            <button className="quiet" style={{ marginTop: 12 }} onClick={() => setShowAllActions((v) => !v)}>
              {showAllActions
                ? 'Hide the actions that are charged but not advertised'
                : `Show ${unlistedActions.length} more that are charged but not advertised`}
            </button>
          ) : null}
        </div>

        {/* -- Where these prices are live ------------------------------------ */}
        <div className="sheet-sec">
          <h3>Where these prices are live</h3>
          <div className="line">
            <span className="what">
              The website
              <span className="note">
                {siteCurrency
                  ? `showing ${siteCurrency} prices to visitors`
                  : 'no published price list to show'}
              </span>
            </span>
            <span className="lead" />
            <span className="fig">
              {siteCurrency === cur ? (
                <span className="muted" style={{ fontWeight: 400 }}>showing this list</span>
              ) : live && currencies.length > 1 ? (
                <button className="quiet" onClick={() => onPresentOnSite(cur)}>
                  Show {cur} instead
                </button>
              ) : (
                <span className="muted" style={{ fontWeight: 400 }}>—</span>
              )}
            </span>
          </div>
          <div className="line">
            <span className="what">
              Stripe
              <span className="note">where the card is actually charged</span>
            </span>
            <span className="lead" />
            <span className="fig" style={{ fontWeight: 400 }}>
              {stripe == null ? (
                <span className="muted">checking…</span>
              ) : stripeReady === stripe.length ? (
                <span className="muted">all {stripe.length} prices in place</span>
              ) : (
                <span className="muted">
                  {stripe.length - stripeReady} of {stripe.length} prices not created yet
                </span>
              )}
            </span>
          </div>
          <p className="hint">
            Checkout charges these prices once they exist in Stripe. Until then it keeps charging the
            prices it had before. Preview shows what would be created; nothing is sent until you apply.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn" disabled={syncBusy} onClick={() => void runSync(false)}>
              {syncBusy && !sync?.applied ? 'Checking…' : 'Preview Stripe changes'}
            </button>
            {sync && !sync.applied && sync.prices.some((p) => p.would_create) ? (
              <button className="btn primary" disabled={syncBusy} onClick={() => void runSync(true)}>
                Create them in Stripe ({sync.mode})
              </button>
            ) : null}
          </div>
          {syncError ? <p className="hint" style={{ color: 'var(--danger, #b42318)' }}>{syncError}</p> : null}
          {sync ? (
            <div style={{ marginTop: 8 }}>
              <p className="hint">
                {sync.applied ? 'Done in' : 'Would change in'} <b>{sync.mode}</b> Stripe:
                {sync.prices.length === 0 ? ' nothing, every price is already there.' : null}
              </p>
              {sync.prices.map((p) => (
                <div className="line" key={`${p.price_kind}:${p.plan_key ?? ''}`}>
                  <span className="what">
                    {p.price_kind.replace('_', ' ')}{p.plan_key ? ` · ${p.plan_key}` : ''}
                    <span className="note">{p.skipped ?? p.reason ?? ''}</span>
                  </span>
                  <span className="lead" />
                  <span className="fig" style={{ fontWeight: 400 }}>
                    {p.created ? `created ${p.created}` : p.would_create ? 'would create' : 'skipped'}
                  </span>
                </div>
              ))}
              {sync.errors.map((e) => <p key={e} className="hint">{e}</p>)}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
